import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Database } from "lucide-react";
import { cn } from "@/lib/utils";
import { type TableSchema, clusterColors } from "./schema-data";

interface EntityCardProps {
  table: TableSchema;
  isHighlighted: boolean;
  isDimmed: boolean;
  onClick: () => void;
}

export default function EntityCard({
  table,
  isHighlighted,
  isDimmed,
  onClick,
}: EntityCardProps) {
  const cluster = clusterColors[table.cluster];

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Card
          onClick={onClick}
          className={cn(
            "cursor-pointer select-none transition-all duration-200 border-2 px-3 py-2 w-44",
            cluster.bg,
            isHighlighted
              ? "ring-2 ring-primary shadow-lg scale-105 border-primary"
              : cluster.border,
            isDimmed && "opacity-30 scale-95"
          )}
        >
          <div className="flex items-center gap-2">
            <Database className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-sm font-semibold truncate">{table.label}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {table.columns.filter((c) => c.pk || c.fk).length > 0 && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                {table.columns.filter((c) => c.pk).length} PK
                {table.columns.filter((c) => c.fk).length > 0 &&
                  ` / ${table.columns.filter((c) => c.fk).length} FK`}
              </Badge>
            )}
            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
              {table.columns.length} cols
            </Badge>
          </div>
        </Card>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="right" align="start">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">{table.label}</h4>
            <Badge variant="outline" className="text-xs capitalize">
              {cluster.label}
            </Badge>
          </div>
          <div className="space-y-1">
            {table.columns.map((col) => (
              <div
                key={col.name}
                className="flex items-center gap-2 text-xs py-0.5"
              >
                {col.pk && (
                  <Badge className="bg-blue-500 text-white text-[10px] px-1 py-0 h-4">
                    PK
                  </Badge>
                )}
                {col.fk && (
                  <Badge className="bg-amber-500 text-white text-[10px] px-1 py-0 h-4">
                    FK
                  </Badge>
                )}
                {!col.pk && !col.fk && (
                  <span className="w-7" />
                )}
                <span className="font-mono font-medium">{col.name}</span>
                <span className="text-muted-foreground ml-auto">
                  {col.type}
                  {col.nullable && "?"}
                </span>
              </div>
            ))}
          </div>
          {table.columns.some((c) => c.description) && (
            <div className="border-t pt-1.5 space-y-0.5">
              {table.columns
                .filter((c) => c.description)
                .map((c) => (
                  <p key={c.name} className="text-[10px] text-muted-foreground">
                    <span className="font-mono">{c.name}</span>: {c.description}
                  </p>
                ))}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
